// 主人公が「歩いているか」を x の変化から判定する。
//   Spine (lunaire-doll) の walk / walk_back へ切り替えるためだけに使う。
//
// 🔴 2026-07-28: このファイルは旧 `player_sprite.ts` の残り。
//   旧 LUNAIRE スプライト (lunaire-v2 の 1枚絵 / 動作別フレーム) のロードと描画は
//   本番から撤去した (Spine に全アニメが author 済で、通信を遮断して実測しても
//   旧経路は一度も描画されなかった)。素材は public/sprites/archived/lunaire-v2/。
//   ここに残したのは「歩き検出」だけ = 絵を1枚も読まない。

// requestAnimationFrame と fixed timestep のタイミング差で dx = 0 の frame が連続発生する問題に対応するため、
// hysteresis (150ms 持続タイマー) で short gap を吸収する

let lastPlayerX: number | null = null
let walkingElapsedSec = 0
let walkingPersistenceTimer = 0
let lastWalkDirSign = 1  // 直近の移動方向 (+1=ワールド右 / -1=ワールド左)。dx=0 の間も保持。
const WALK_THRESHOLD_PX = 0.05  // 1 frame でも僅かに動いていたら walking とみなす
const WALK_PERSISTENCE_SEC = 0.15  // dx = 0 が短時間続いても walking 状態を維持

export interface PlayerAnimState {
  isWalking: boolean
  walkingElapsedSec: number
  /** 移動方向の符号 (+1=ワールド右 / -1=ワールド左)。facing と比較して前進/後退を判定する。 */
  dirSign: number
}

export function updatePlayerAnimState(currentX: number, dt: number): PlayerAnimState {
  if (lastPlayerX === null) {
    lastPlayerX = currentX
    return { isWalking: false, walkingElapsedSec: 0, dirSign: lastWalkDirSign }
  }
  const signedDx = currentX - lastPlayerX
  const dxObserved = Math.abs(signedDx) > WALK_THRESHOLD_PX
  if (dxObserved) {
    walkingPersistenceTimer = WALK_PERSISTENCE_SEC
    lastWalkDirSign = Math.sign(signedDx)  // 動いた瞬間だけ方向を更新 (持続中は維持)
  } else {
    walkingPersistenceTimer = Math.max(0, walkingPersistenceTimer - dt)
  }
  const isWalking = walkingPersistenceTimer > 0
  if (isWalking) {
    walkingElapsedSec += dt
  } else {
    walkingElapsedSec = 0
  }
  lastPlayerX = currentX
  return { isWalking, walkingElapsedSec, dirSign: lastWalkDirSign }
}

// 内部 state をクリアする。 新しい戦闘が始まった時 (state.time の巻き戻り) に render.ts が呼ぶ。
//   これを呼ばないと、 前の戦闘の最後の x が lastPlayerX に残り、 リスポーン位置へのワープを
//   「歩き」と誤検知して walk ループが一瞬出る。
export function resetPlayerAnimState(): void {
  lastPlayerX = null
  walkingElapsedSec = 0
  walkingPersistenceTimer = 0
  lastWalkDirSign = 1
}

// テスト用の別名 (既存テストの呼び出し名を保つ)
export const _resetPlayerAnimStateForTest = resetPlayerAnimState
